import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CommonDataService } from './commonDataService';
import { AppContext } from '../Shared/common';
import { Global } from '../Shared/global';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApplicationConfiguration } from '../Models/ApplicationConfiguration';
import { CommonSetting } from '../Models/Common/CommonSettings';

export interface ThemeConfig {
  themeName?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  fontFamily?: string;
  fontSize?: string;
  borderRadius?: string;
  logoUrl?: string;
  faviconUrl?: string;
  appTitle?: string;
  appDescription?: string;
  loginLeftBoxHtml?: string;
  loginLeftCssClass?: string;
  loginFooterHtml?: string;
  customCss?: string;
  [key: string]: any;
}

export interface ThemeDefinition {
  name: string;
  className: string;
  displayName: string;
  description?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AppConfigService {
  // Available themes
  private availableThemes: ThemeDefinition[] = [
    {
      name: 'fitspeex-dark',
      className: 'theme-fitspeex-dark',
      displayName: 'Dark',
      description: 'Dark theme with cyan accents'
    },    
    {
      name: 'red',
      className: 'power-red',
      displayName: 'Red',
      description: 'Bold red theme with light background'
    },
    //{
    //  name: 'violet',
    //  className: 'theme-violet',
    //  displayName: 'Violet',
    //  description: 'Purple gradient theme'
    //},
    //{
    //  name: 'ocean',
    //  className: 'theme-ocean',
    //  displayName: 'Ocean',
    //  description: 'Deep ocean theme with cyan accents'
    //},
    //{
    //  name: 'forest',
    //  className: 'theme-forest',
    //  displayName: 'Forest',
    //  description: 'Forest green theme'
    //},
    //{
    //  name: 'light',
    //  className: 'theme-light',
    //  displayName: 'Light',
    //  description: 'Clean light theme with blue accents'
    //}
  ];

  private currentTheme = new BehaviorSubject<ThemeDefinition>(this.availableThemes[0]);
  public currentTheme$: Observable<ThemeDefinition> = this.currentTheme.asObservable();

  private appConfig = new BehaviorSubject<ApplicationConfiguration>({
    CustomSettings: null,
    ProductSettings: [],
    InvoiceSettings: [],
    Settings: []
  });
  public appConfig$: Observable<ApplicationConfiguration> = this.appConfig.asObservable();

  private isConfigLoaded = false;
  private appConfigLoadPromise: Promise<ApplicationConfiguration> | null = null;
  private previousThemeClass: string = 'theme-fitspeex-dark'; // Track previous theme

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private commonDataService: CommonDataService,
    private appContext: AppContext
  ) {
    this.initializeStoredTheme();
  }

  /**
   * Initialize theme from local storage
   */
  private initializeStoredTheme(): void {
    const storedThemeName = localStorage.getItem('selectedTheme');
    if (storedThemeName) {
      const theme = this.availableThemes.find(t => t.name === storedThemeName);
      if (theme) {
        this.currentTheme.next(theme);
        this.previousThemeClass = theme.className;
      }
    }
  }

  /**
   * Load theme configuration from API
   */
  loadAppConfig(): Promise<ApplicationConfiguration> {
    if (this.appConfigLoadPromise) {
      return this.appConfigLoadPromise;
    }

    if (this.isConfigLoaded) {
      return Promise.resolve(this.appConfig.value);
    }

    this.appConfigLoadPromise = this.fetchAndApplyTheme();
    return this.appConfigLoadPromise;
  }

  /**
   * Fetch theme config from API and apply it
   */
  private fetchAndApplyTheme(): Promise<ApplicationConfiguration> {
    const endpoint = Global.BASE_USER_ENDPOINT() + Global.CommonSettingsController;

    return this.commonDataService.getData(endpoint)
      .then((config: CommonSetting[]) => {
        Global.Log('[AppConfigService] App config received:', config);

        if (config && config.length > 0) {
          const appConfig: ApplicationConfiguration = this.parseThemeConfig(config);

          // Apply theme name from config if provided
          if (appConfig.CustomSettings.ThemeName) {
            this.setTheme(appConfig.CustomSettings.ThemeName);
          } else {
            this.applyCurrentTheme();
          }

          this.appConfig.next(appConfig);
          this.isConfigLoaded = true;
          this.appContext.AppConfigurations = appConfig;
          return appConfig;
        }

        this.applyCurrentTheme();
        this.isConfigLoaded = true;
        return {
          CustomSettings: null,
          ProductSettings: [],
          InvoiceSettings: [],
          Settings: []
        };
      })
      .catch(error => {
        Global.LogError('[AppConfigService] Error loading app config:', error);
        this.applyCurrentTheme();
        this.isConfigLoaded = true;
        return {
          CustomSettings: null,
          ProductSettings: [],
          InvoiceSettings: [],
          Settings: []
        };
      });
  }

  /**
   * Parse theme configuration from custom settings
   */
  private parseThemeConfig(customSettings: CommonSetting[]): ApplicationConfiguration {
    const config: ApplicationConfiguration = {
      CustomSettings: {},
      ProductSettings: [],
      InvoiceSettings: [],
      Settings: []
    };

    customSettings.forEach(setting => {
      switch (setting.Key) {
        case 'ThemeName':
          config.CustomSettings.themeName = setting.Value;
          break;
        case 'PrimaryColor':
          config.CustomSettings.primaryColor = setting.Value;
          break;
        case 'SecondaryColor':
          config.CustomSettings.secondaryColor = setting.Value;
          break;
        case 'AccentColor':
          config.CustomSettings.accentColor = setting.Value;
          break;
        case 'FontFamily':
          config.CustomSettings.fontFamily = setting.Value;
          break;
        case 'FontSize':
          config.CustomSettings.fontSize = setting.Value;
          break;
        case 'BorderRadius':
          config.CustomSettings.borderRadius = setting.Value;
          break;
        case 'LogoUrl':
          config.CustomSettings.logoUrl = setting.Value;
          break;
        case 'FaviconUrl':
          config.CustomSettings.faviconUrl = setting.Value;
          break;
        case 'AppTitle':
          config.CustomSettings.appTitle = setting.Value;
          break;
        case 'AppDescription':
          config.CustomSettings.appDescription = setting.Value;
          break;
        case 'LoginBoxHtml':
          config.CustomSettings.loginLeftBoxHtml = setting.Value;
          break;
        case 'LoginLeftCssClass':
          config.CustomSettings.loginLeftCssClass = setting.Value;
          break;
        case 'LoginFooterHtml':
          config.CustomSettings.loginFooterHtml = setting.Value;
          break;
        case 'CustomCss':
          config.CustomSettings.customCss = setting.Value;
          break;
      }
    });

    return config;
  }

  /**
   * Set theme by name
   */
  setTheme(themeName: string): void {
    const theme = this.availableThemes.find(t => t.name === themeName || t.className === themeName);

    if (theme) {
      this.applyTheme(theme);
      this.currentTheme.next(theme);
      localStorage.setItem('selectedTheme', theme.name);
      Global.Log('[ThemeService] Theme changed to:', theme.displayName);
    } else {
      Global.LogWarning('[ThemeService] Theme not found:', themeName);
    }
  }

  /**
   * Apply current theme
   */
  private applyCurrentTheme(): void {
    const currentTheme = this.currentTheme.value;
    this.applyTheme(currentTheme);
  }

  /**
   * Apply theme to the document
   */
  private applyTheme(theme: ThemeDefinition): void {
    // Remove previous theme class
    if (this.previousThemeClass) {
      this.document.documentElement.classList.remove(this.previousThemeClass);
    }

    // Add new theme class
    this.document.documentElement.classList.add(theme.className);
    this.previousThemeClass = theme.className;

    // Also set on body for broader compatibility
    this.document.body.classList.remove(...this.availableThemes.map(t => t.className));
    this.document.body.classList.add(theme.className);

    Global.Log('[ThemeService] Theme applied:', theme.displayName);
  }

  /**
   * Get all available themes
   */
  getAvailableThemes(): ThemeDefinition[] {
    return [...this.availableThemes];
  }

  /**
   * Get current theme
   */
  getCurrentTheme(): ThemeDefinition {
    return this.currentTheme.value;
  }

  /**
   * Get current theme config
   */
  getThemeConfig(): ThemeConfig {
    return this.appConfig.value;
  }

  /**
   * Check if theme is loaded
   */
  isLoaded(): boolean {
    return this.isConfigLoaded;
  }

  /**
   * Reset theme to defaults
   */
  resetTheme(): void {
    this.isConfigLoaded = false;
    this.appConfigLoadPromise = null;
    this.appConfig.next({
      CustomSettings: null,
      ProductSettings: [],
      InvoiceSettings: [],
      Settings: []
    });
    this.setTheme('fitspeex-dark');
  }
}
